import { reactive, ref, Ref } from 'vue'
import { ApiResponse, ApiPaymentResponse, ApiPaymentResultResponse, NuxeoPageProviderResponse, NuxeoUpdateUserResponse, NuxeoDocumentsDownloadedResponse } from '@/contracts/api/ResponseContracts'
import store from '@/store'
import axios, { AxiosInstance } from 'axios'

// Development-only diagnostics. NODE_ENV is inlined by webpack, so these calls
// are dropped from production builds: an axios error's config carries the
// request headers, including the Authorization bearer, and response payloads
// are the customer's own data — neither belongs in a production console.
function debugLog(...args: unknown[]): void
{
    if(process.env.NODE_ENV !== 'production')
    {
        console.log(...args)
    }
}

// Build an Authorization: Bearer header from the OAuth access token issued
// via the PHP proxy's /auth/login exchange (stored on the auth slice in
// Vuex). Returned as a headers fragment so callers can spread it into axios
// config. Empty object when the user is not signed in or no token is present
// (auth-required endpoints will then get the standard 401/403 from the
// proxy/Nuxeo).
// See §3.2 in docs/proposals/post-golive-payment-integrity-fix.md.
function buildAuthHeader(): Record<string, string>
{
    const token = store.getters['auth/getToken'] as string | undefined
    if(token)
    {
        return { Authorization: 'Bearer ' + token }
    }
    return {}
}

// Single-flight refresh of the OAuth token pair via the PHP proxy (the
// refresh grant needs the OAuth client secret, so it cannot run in the
// browser — Q20 in docs/proposals/irm-questions-register.md). Resolves true
// when new tokens are stored; false when the refresh token is rejected, at
// which point the auth slice is cleared and the router guards send the
// signed-out user back to Login on their next navigation (Q21).
let refreshPromise: Promise<boolean> | null = null

function refreshAuthTokens(): Promise<boolean>
{
    if(refreshPromise === null)
    {
        refreshPromise = axios.post(process.env.VUE_APP_API_DOMAIN + '/auth/refresh',
        {
            refreshToken: store.getters['auth/getRefreshToken']
        })
        .then(function(response)
        {
            store.commit('auth/setTokens',
            {
                accessToken: response.data.tokens.accessToken,
                refreshToken: response.data.tokens.refreshToken
            })
            return true
        })
        .catch(function()
        {
            store.commit('auth/logout')
            return false
        })
        .finally(function()
        {
            refreshPromise = null
        })
    }
    return refreshPromise
}

// 401 → refresh → retry-once interceptor for axios instances that send
// Authorization: Bearer. Transparent to the caller: on access-token expiry
// the request is replayed with the refreshed token; if the refresh fails the
// original 401 propagates to the caller's normal error path (Q21).
function attachAuthRefresh(instance: AxiosInstance): AxiosInstance
{
    instance.interceptors.response.use(undefined, function(error)
    {
        const config = error.config
        if(error.response && error.response.status === 401 && config && !config._authRetried && store.getters['auth/getRefreshToken'])
        {
            config._authRetried = true
            return refreshAuthTokens().then(function(refreshed)
            {
                if(!refreshed)
                {
                    return Promise.reject(error)
                }
                config.headers = Object.assign({}, config.headers, buildAuthHeader())
                return instance.request(config)
            })
        }
        return Promise.reject(error)
    })
    return instance
}

export function axiosGet(url: string, params?: Record<string, unknown>): ApiResponse
{
    const data = reactive({} as ApiResponse)
    const axiosInstance = axios.create(
    {
        headers:
        {
            skipAggregates: 'true',
            'enrichers-document': 'thumbnail, permissions, highlight',
            'translate-directoryEntry': 'label',
            'fetch-aggregate': 'key',
            properties: 'hmcts_grant_schema',
            'fetch-document': 'properties'
        }
    })

    let queryString = ''
    if(params)
    {
        queryString = Object.keys(params).map(key => key + '=' + params[key]).join('&')
        // Log in non-production only (was unconditional — IRM review §1.7):
        if(process.env.NODE_ENV !== 'production')
        {
            debugLog(queryString)
        }
    }

    axiosInstance.get(process.env.VUE_APP_API_DOMAIN + url + '?' + queryString).then((response) =>
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            debugLog(response.data)
        }
        // Set data:
        Object.assign(data, response.data)
    }).catch(function(error)
    {
        handleError(error)
    })

    return data
}

// Nuxeo blob URLs (file:content.data) carry the emitting server's own host
// and context path — only the /nxfile/... path is meaningful to the proxy
// (VUE_APP_API_DOMAIN already ends in /csp). Splitting on 'csp' broke on
// hosts whose NAME contains "csp" (dit02: *-a-csp.com → '.com/'); /nxfile/
// can only ever match the path.
export function nuxeoBlobPath(url: string): string
{
    const index = url.indexOf('/nxfile/')
    return index >= 0 ? url.substring(index) : ''
}

export function axiosDownload(url: string): Ref
{
    const data = ref('')
    // Signed-in document download (My Orders). Forward the Bearer so the blob is
    // served under the caller's own Nuxeo ACL, not the lent admin service
    // account: the /nxfile pass-through no longer lends admin for grant/will
    // documents (IRM security review 2026-09-24). attachAuthRefresh retries once
    // via /auth/refresh on access-token expiry.
    const axiosInstance = attachAuthRefresh(axios.create(
    {
        responseType: 'blob',
        headers: buildAuthHeader()
    }))

    axiosInstance.get(process.env.VUE_APP_API_DOMAIN + url).then((response) =>
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            debugLog(response.data)
        }
        // Set data:
        data.value = response.data
    }).catch(function(error)
    {
        handleError(error)
    })

    return data
}

export function axiosGetPayment(url: string): ApiPaymentResponse
{
    const data = reactive({} as ApiPaymentResponse)
    const axiosInstance = axios.create({})

    axiosInstance.get(process.env.VUE_APP_API_DOMAIN + url).then((response) =>
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            debugLog(response.data)
        }
        // Set data:
        Object.assign(data, response.data)
    }).catch(function(error)
    {
        handleError(error)
    })

    return data
}

export function axiosPostPayment(url: string, postData: Record<string, unknown>): ApiPaymentResponse
{
    const data = reactive({} as ApiPaymentResponse)
    // Forward Authorization: Bearer <token> — /payment/prepare is fail-closed
    // (§3.11) and derives the customer identity from the verified token rather
    // than the request body (register Q34), so it rejects an unauthenticated
    // caller. attachAuthRefresh transparently retries once via /auth/refresh on
    // access-token expiry (Q21).
    const axiosInstance = attachAuthRefresh(axios.create({ headers: buildAuthHeader() }))

    axiosInstance.post(process.env.VUE_APP_API_DOMAIN + url, postData).then((response) =>
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            debugLog(response.data)
        }
        // Set data:
        Object.assign(data, response.data)
    }).catch(function(error)
    {
        handleError(error)
        // Surface the failure so Pay.vue's watcher fires and routes the user
        // back to the basket with an error — otherwise the reactive result
        // never changes and they hang on the "Redirecting to payment..."
        // spinner. (The calendar-order prep that used to guard this path is
        // now server-side inside /payment/prepare.)
        Object.assign(data, { status: (error.response && error.response.status) || 500, message: 'Payment service unavailable.', errorCode: 'NETWORK_ERROR' })
    })

    return data
}

export function axiosPostPaymentResult(url: string, postData: Record<string, unknown>): ApiPaymentResultResponse
{
    const data = reactive({} as ApiPaymentResultResponse)
    // Forward Authorization: Bearer <token> so the PHP proxy can pass the
    // user's OAuth/JWT token to Nuxeo, which lets GetOrderStatusOperation run
    // its ownership check against the real caller rather than the service
    // account (§3.2). attachAuthRefresh transparently retries once through
    // the proxy's /auth/refresh on access-token expiry (Q21).
    const axiosInstance = attachAuthRefresh(axios.create({ headers: buildAuthHeader() }))

    axiosInstance.post(process.env.VUE_APP_API_DOMAIN + url, postData).then((response) =>
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            debugLog(response.data)
        }
        // Set data:
        Object.assign(data, response.data)
    }).catch(function(error)
    {
        handleError(error)
        Object.assign(data, { status: 500, errorCode: 'NETWORK_ERROR', transactionStatus: '', transactionReference: '' })
    })

    return data
}

// Signed-in orders read via the proxy's fail-closed /orders route (which
// fronts GetAllOrderedGrantsOperation). The Bearer token is forwarded so the
// op runs as the real signed-in principal, and the proxy derives customerID
// from that verified token — never from this request body — so the call
// cannot read another customer's orders (register Q34; closes IRM's
// 2026-07-15 report of AS_AC_Get_All_Ordered_Grants firing while signed
// out). attachAuthRefresh transparently retries once via /auth/refresh on
// access-token expiry (Q21).
export function axiosPostOrders(postData: Record<string, unknown>, callback: (response: NuxeoPageProviderResponse) => void): void
{
    const axiosInstance = attachAuthRefresh(axios.create({ headers: buildAuthHeader() }))

    axiosInstance.post(process.env.VUE_APP_API_DOMAIN + '/orders', postData).then((response) =>
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            debugLog(response.data)
        }
        callback(response.data)
    }).catch(function(error)
    {
        handleError(error)
    })
}

// Signed-in customer's own order numbers via the proxy's fail-closed
// /order-numbers route. Replaces the former client-side NXQL in
// nuxeoCustomerOrders(): the query now runs server-side with customerID pinned
// to the verified token, so the browser never builds a query nor is lent the
// admin service account (IRM security review 2026-09-24). The Bearer is
// forwarded; attachAuthRefresh retries once via /auth/refresh on token expiry.
export function axiosPostOrderNumbers(callback: (response: NuxeoPageProviderResponse) => void): void
{
    const axiosInstance = attachAuthRefresh(axios.create({ headers: buildAuthHeader() }))

    axiosInstance.post(process.env.VUE_APP_API_DOMAIN + '/order-numbers', {}).then((response) =>
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            debugLog(response.data)
        }
        callback(response.data)
    }).catch(function(error)
    {
        handleError(error)
    })
}

// Signed-in account self-service update (email and/or password) via the
// proxy's authenticated /account/update-user route. The Bearer identifies the
// caller; the proxy pins the target to that verified identity and performs the
// update under the service account (UpdateExternalUserOperation is blacklisted
// from the browser pass-through), so a caller can only ever update their own
// record — never another user's, and never unauthenticated. attachAuthRefresh
// retries once via /auth/refresh on access-token expiry (Q21).
// Record a customer download so Nuxeo starts the item's availability window,
// via the authenticated /orders/downloaded route. The underlying operation
// (CheckAndSetExpiryDateForOrderItemsOperation) MUST run as the signed-in
// customer, not the service account: as an admin it deliberately skips setting
// the expiry (IRM/Pawel, 2026-09-11), so a header-less call left the window
// unset. The raw op is blacklisted from the browser pass-through.
// attachAuthRefresh retries once via /auth/refresh on token expiry.
export function axiosPostDownloadRecorded(input: string, params: Record<string, unknown>, callback: (response: NuxeoDocumentsDownloadedResponse) => void): void
{
    const axiosInstance = attachAuthRefresh(axios.create({ headers: buildAuthHeader() }))

    axiosInstance.post(process.env.VUE_APP_API_DOMAIN + '/orders/downloaded', { input, params }).then((response) =>
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            debugLog(response.data)
        }
        callback(response.data)
    }).catch(function(error)
    {
        handleError(error)
    })
}

export function axiosPostAccountUpdate(postData: Record<string, unknown>, callback: (response: NuxeoUpdateUserResponse) => void): void
{
    const axiosInstance = attachAuthRefresh(axios.create({ headers: buildAuthHeader() }))

    axiosInstance.post(process.env.VUE_APP_API_DOMAIN + '/account/update-user', postData).then((response) =>
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            debugLog(response.data)
        }
        callback(response.data)
    }).catch(function(error)
    {
        handleError(error)
    })
}

// Handle error in axios:
function handleError(error: any): void
{
    // Look for response in error:
    if(error.response)
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            // debugLog(error.response.data)
            debugLog(error.response.status)
            debugLog(error.response.headers)
        }
    }
    // Request was made, but no response received:
    else if(error.request)
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            debugLog(error.request)
        }
    }
    // Something happened in setting up the request that triggered an error:
    else
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            debugLog('Error', error.message)
        }
    }
    // Log in non-production:
    if(process.env.NODE_ENV !== 'production')
    {
        debugLog(error.config)
    }
}
